'use-client';
import React, { useLayoutEffect } from 'react';
import MenuContent from '../../../Menu/MenuContent/viewer/MenuContent';
import { useMenuContext } from '../../../Menu/viewer/MenuContext';
import type { HamburgerMenuContentProps } from '../HamburgerMenuContent.types';
import { HorizontalDropdown } from '../../../Menu/viewer/components/HorizontalDropdown/HorizontalDropdown';
import { VerticalDropdown } from '../../../Menu/viewer/components/VerticalDropdown/VerticalDropdown';
import { useHamburgerMenuContext } from '../../HamburgerMenuContext';

const HamburgerMenuContent: React.FC<HamburgerMenuContentProps> = props => {
  const {
    id,
    onItemMouseIn,
    onItemMouseOut,
    onItemClick,
    onItemDblClick,
    containerRootClassName,
  } = props;
  const { isHamburgerMenuOpened } = useMenuContext();
  const { prehydrationToggleId } = useHamburgerMenuContext();

  // During SSR/pre-hydration the content must be in the DOM so CSS-only
  // toggles work. After hydration it must unmount when closed — otherwise
  // scroll-button children with `visibility:visible` leak through the
  // overlay's `visibility:hidden` (CSS visibility on children overrides the
  // parent).
  const [isHydrated, setIsHydrated] = React.useState(false);
  useLayoutEffect(() => setIsHydrated(true), []);

  // Detect if the menu was opened via CSS checkbox before React hydrated.
  // isHydrated (useLayoutEffect) fires before isHamburgerMenuOpened syncs
  // (useEffect → updateComponentPropsInViewer → context), so without this
  // check the content unmounts for 1-2 frames and then remounts — causing a
  // visible jump. Same pattern as HamburgerOverlay.tsx.
  const prehydrationOpenRef = React.useRef<boolean | null>(null);
  if (prehydrationOpenRef.current === null) {
    if (prehydrationToggleId && typeof document !== 'undefined') {
      const checkbox = document.getElementById(
        prehydrationToggleId,
      ) as HTMLInputElement | null;
      prehydrationOpenRef.current = !!checkbox?.checked;
    } else {
      prehydrationOpenRef.current = false;
    }
  }
  if (isHamburgerMenuOpened) {
    prehydrationOpenRef.current = false;
  }

  if (
    !isHamburgerMenuOpened &&
    !prehydrationOpenRef.current &&
    (!prehydrationToggleId || isHydrated)
  ) {
    return null;
  }

  return (
    <MenuContent
      id={id}
      className={containerRootClassName}
      // TODO: create MenuItemsEventHandlerPropsSDK and extend it in Velo for these functions to work
      onItemMouseIn={onItemMouseIn}
      onItemMouseOut={onItemMouseOut}
      onItemClick={onItemClick}
      onItemDblClick={onItemDblClick}
      HorizontalDropdown={HorizontalDropdown}
      VerticalDropdown={VerticalDropdown}
      submenuToggleIdPrefix={prehydrationToggleId ? id : undefined}
    />
  );
};

export default HamburgerMenuContent;
